import { useRef } from "react";
import { FileX2, TrendingDown, Clock, FileQuestion, HelpCircle } from "lucide-react";
import { Reveal, Tag, H2 } from "@/components/sections/shared";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";
import FoldText from "@/components/FoldText/FoldText";

const pains = [
  { icon: FileX2, title: "Claim Denied", copy: "You received a denial letter and have no idea if it is valid." },
  { icon: TrendingDown, title: "Settlement Too Low", copy: "The offer does not come close to covering the damage or loss." },
  { icon: Clock, title: "Claim Taking Too Long", copy: "You keep getting transferred, delayed, or ignored." },
  { icon: FileQuestion, title: "Confusing Insurance Language", copy: "The policy language makes it hard to know what is actually covered." },
  { icon: HelpCircle, title: "Not Sure What To Do Next", copy: "You do not know whether to appeal, escalate, hire help, or accept the offer." },
];

export default function Problem() {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();

  return (
    <section id="problem" data-testid="problem-section" className="relative bg-[#0A0A0C] py-24 md:py-32 overflow-hidden">
      <GlobalSpotlight
        gridRef={gridRef}
        disableAnimations={isMobile}
        glowColor="230, 57, 70"
        spotlightRadius={280}
      />
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(138,43,226,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(138,43,226,0.4) 1px, transparent 1px)",
          backgroundSize: "76px 76px",
        }}
      />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-14">
        <Reveal>
          <Tag testId="problem-tag">The Problem</Tag>
          <H2 className="max-w-2xl">
            <FoldText
              text="Insurance gets confusing right when you need it most."
              splitBy="word"
              trigger="scroll"
              fontSize="inherit"
              fontWeight="inherit"
              color="inherit"
            />
          </H2>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] font-light leading-relaxed">
            You paid your premiums. You filed the claim. You expected help. Then came the delays,
            confusing letters, low settlement offers, unanswered calls, or outright denial. Most
            people do not know whether the insurance company is being fair or simply hoping they
            will give up. That is where Inssux begins.
          </p>
        </Reveal>

        <div ref={gridRef} className="bento-section mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pains.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <ParticleCard
                testId={`pain-card-${i}`}
                disableAnimations={isMobile}
                glowColor="230, 57, 70"
                className="magic-bento-card magic-bento-card--border-glow group relative h-full bg-[#1A1A1D] border border-white/10 p-7 hover:border-[#E63946]/50 transition-colors duration-300"
              >
                <div className="absolute top-4 right-4 font-mono-file text-[9px] tracking-[0.25em] text-[#E63946]/60 border border-[#E63946]/30 px-2 py-0.5 rotate-2">
                  FLAGGED
                </div>
                <p.icon size={22} className="text-[#E63946]" />
                <h3 className="mt-4 font-display text-lg tracking-wide text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-[#A1A1AA] font-light leading-relaxed">{p.copy}</p>
              </ParticleCard>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <ParticleCard
              disableAnimations={isMobile}
              glowColor="138, 43, 226"
              className="magic-bento-card magic-bento-card--border-glow h-full flex flex-col justify-center bg-[#8A2BE2]/[0.08] border border-[#8A2BE2]/40 p-7"
            >
              <p className="font-serif-jack italic text-xl text-[#FFD166]/90 leading-snug">
                "I've seen how this works from the inside. My job is to help you slow things down,
                understand the language, and see your next logical step."
              </p>
              <p className="mt-3 font-mono-file text-[10px] tracking-[0.25em] text-[#9D4EDD]">
                JACK D. HAPSBURG
              </p>
            </ParticleCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
